// Mobile UI route table — the Flutter navigation graph.
//
// Flutter pushes MaterialPageRoutes; here each of those destinations is a route
// so the Android hardware back button and deep links behave the same way.
//
//   /splash                  splashScreen.dart      (boot gate)
//   /login /signup /forgot   the three auth screens
//   /blocked                 blockedScreen.dart
//   /home?tab=N              homeScreen.dart        (4 bottom tabs)
//   /rooms/:locationId       roomsScreen.dart
//   /room/:roomId/devices    devicesScreen.dart
//   /device/:uniqueNumber    screenRedirector()     -> per-type control screen
//
// Screens that are identical on both form factors (Wall Mode, per-room Scenes,
// Profile, About, Schedules) are reused from ui/desktop rather than duplicated —
// they read the same core logic, which is the whole point of the split.

import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { setPushOpenHandler, pushTargetPath } from '../../core/push/registerPush';
import { useApp } from '../../core/state/AppContext';
import { kioskLandingRoute } from '../../core/wall/kioskMode';
import { MessageDialogProvider } from './components/MessageDialog';

import SplashScreen from './screens/SplashScreen';
import BlockedScreen from './screens/BlockedScreen';
import LoginScreen from './screens/LoginScreen';
import SignUpScreen from './screens/SignUpScreen';
import ForgotPasswordScreen from './screens/ForgotPasswordScreen';
import HomeScreen from './screens/HomeScreen';
import RoomsScreen from './screens/RoomsScreen';
import DevicesScreen from './screens/DevicesScreen';
import DeviceScreen from './screens/device/DeviceScreen';
import LocationDataScreen from './screens/LocationDataScreen';
import WifiOnboardScreen from './screens/WifiOnboardScreen';
import AddDeviceScreen from './screens/AddDeviceScreen';
import ScanShareScreen from './screens/ScanShareScreen';
import BleNodeSetupScreen from './screens/BleNodeSetupScreen';
import BleTestScreen from './screens/BleTestScreen';
import MqttConsoleScreen from './screens/MqttConsoleScreen';
import TuyaTestingScreen from './screens/TuyaTestingScreen';
import FirmwareScreen from './screens/FirmwareScreen';

// Shared with the desktop shell — same component, same core logic.
import RoomScenesScreen from '../desktop/pages/scenes/RoomScenesScreen';
import ProfileScreen from '../desktop/pages/ProfileScreen';
import AboutScreen from '../desktop/pages/AboutScreen';
import SchedulesScreen from '../desktop/pages/SchedulesScreen';
import MobileShell from './components/MobileShell';
import WallModeScreen from '../desktop/pages/wall/WallModeScreen';
import WallPanelSetupScreen from '../shared/WallPanelSetupScreen';
import AdminToolsLayout from '../shared/AdminToolsLayout';

// ── The admin screens, reused rather than rewritten ────────────────────────
// Same rule as Profile/About/Schedules above: one component, both shells. These
// six existed only in DesktopRoutes, so a Super Admin holding a phone could not
// reach them at all — which is the gap this closes.
//
// ⚠️ FOUR OF THEM ARE BUILT FOR A WIDE SCREEN and they are routed here anyway,
// deliberately: reachable-and-cramped beats unreachable, and they carry their
// own AppBar with a back button so they are self-contained inside MobileShell.
// UserAccess (a four-panel transfer list) and DeviceAdmin (a ~60-field form
// distilled to a wizard) are the two that genuinely want a phone layout of
// their own; until then they scroll. Do not "fix" that by hiding the routes.
import CompaniesScreen from '../desktop/pages/CompaniesScreen';
import DeviceTypesScreen from '../desktop/pages/DeviceTypesScreen';
import DeviceAdminScreen from '../desktop/pages/DeviceAdminScreen';
import UserAccessScreen from '../desktop/pages/UserAccessScreen';
import InstalledDevicesScreen from '../desktop/pages/InstalledDevicesScreen';
import DeviceTrackingScreen from '../desktop/pages/DeviceTrackingScreen';
import RfDevicesScreen from '../desktop/pages/RfDevicesScreen';
import RfSignaturesScreen from '../desktop/pages/RfSignaturesScreen';
import RfLogScreen from '../desktop/pages/RfLogScreen';
import KioskSetupScreen from '../desktop/pages/KioskSetupScreen';

function Protected({ children, bare = false }) {
  const { isAuthed, signingIn } = useApp();
  // Same rule as desktop: a saved sign-in in flight is "not yet decided", not
  // signed-out. Without this the login screen flashes before the session lands.
  if (signingIn) return <SplashScreen />;
  if (!isAuthed) return <Navigate to="/login" replace />;
  // ── The identity strip and company footer live HERE ──────────────────────
  // Attaching them to `Protected` rather than to each screen means every
  // authenticated route has them by construction, and a screen added tomorrow
  // cannot forget. The pre-auth routes below are deliberately not wrapped:
  // there is no user yet to name.
  //
  // `bare` opts a route out — Wall Mode is a kiosk that deliberately fills the
  // panel edge to edge, and chrome on it would be wrong rather than missing.
  return bare ? children : <MobileShell>{children}</MobileShell>;
}

// ── Permission, at the ROUTE, the way the desktop shell has always done it ──
//
// ⚠️ HIDING A TILE IS NOT A GATE. These four routes carried comments reading
// "Super Admin only, gated in MeTab" — and that was the whole of it: MeTab
// simply did not draw the tile. The route stayed live, so any signed-in
// customer reaching /mqtt, /firmware, /tuya-testing or /ble-test by typed URL,
// deep link, restored tab or Back got the screen. Three of the four then
// refused on their own; /ble-test had no check of any kind and rendered.
//
// These are not read-only pages. /firmware reflashes hardware estate-wide,
// /tuya-testing rewrites a device's type and reboots it, /mqtt publishes
// arbitrary commands to any topic.
//
// DesktopRoutes gates each of these at the route AND each screen re-checks
// inside. Mobile now does both too — one layer is one edit away from being
// none.
function AdminOnly({ children, allow }) {
  return allow ? children : <Navigate to="/home" replace />;
}

export default function MobileRoutes() {
  const { isAuthed, isSuperAdmin, canTuyaTest } = useApp();
  const navigate = useNavigate();

  // A tapped push notification opens what it is about (core/push/registerPush).
  useEffect(() => {
    setPushOpenHandler((data) => {
      const path = pushTargetPath(data);
      if (path) navigate(path);
    });
    return () => setPushOpenHandler(null);
  }, [navigate]);

  // The admin tools, each behind its OWN route gate — the tab layout renders
  // these elements as given and adds no permission of its own.
  const adminScreens = {
    '/mqtt': <AdminOnly allow={isSuperAdmin}><MqttConsoleScreen /></AdminOnly>,
    '/device-tracking': <AdminOnly allow={isSuperAdmin}><DeviceTrackingScreen /></AdminOnly>,
    '/tuya-testing': <AdminOnly allow={canTuyaTest}><TuyaTestingScreen /></AdminOnly>,
    '/firmware': <AdminOnly allow={isSuperAdmin}><FirmwareScreen /></AdminOnly>,
    '/device-admin': <AdminOnly allow={isSuperAdmin}><DeviceAdminScreen /></AdminOnly>,
    '/user-access': <AdminOnly allow={isSuperAdmin}><UserAccessScreen /></AdminOnly>,
    '/installed': <AdminOnly allow={isSuperAdmin}><InstalledDevicesScreen /></AdminOnly>,
    '/device-types': <AdminOnly allow={isSuperAdmin}><DeviceTypesScreen /></AdminOnly>,
    '/companies': <AdminOnly allow={isSuperAdmin}><CompaniesScreen /></AdminOnly>,
    '/rf-log': <AdminOnly allow={isSuperAdmin}><RfLogScreen /></AdminOnly>,
    '/rf-signatures': <AdminOnly allow={isSuperAdmin}><RfSignaturesScreen /></AdminOnly>,
    // Firmware bring-up over Bluetooth — had NO check of any kind once.
    '/ble-test': <AdminOnly allow={isSuperAdmin}><BleTestScreen /></AdminOnly>,
  };

  return (
    <MessageDialogProvider>
      <Routes>
        <Route path="/splash" element={<SplashScreen />} />
        <Route path="/blocked" element={<BlockedScreen />} />
        <Route path="/login" element={<LoginScreen />} />
        <Route path="/signup" element={<SignUpScreen />} />
        <Route path="/forgot" element={<ForgotPasswordScreen />} />

        <Route path="/home" element={<Protected><HomeScreen /></Protected>} />
        <Route path="/rooms/:locationId" element={<Protected><RoomsScreen /></Protected>} />
        <Route path="/room/:roomId/devices" element={<Protected><DevicesScreen /></Protected>} />
        <Route path="/device/:uniqueNumber" element={<Protected><DeviceScreen /></Protected>} />
        <Route path="/location/:locationId" element={<Protected><LocationDataScreen /></Protected>} />
        {/* BLE Wi-Fi onboarding — hand a brand-new device its home Wi-Fi. */}
        <Route path="/wifi-setup" element={<Protected><WifiOnboardScreen /></Protected>} />
        {/* Claim an existing device: by its own number, or by a share code.
            Both render AddDeviceScreen, which adapts its wording. */}
        <Route path="/add-device" element={<Protected><AddDeviceScreen /></Protected>} />
        <Route path="/scan-share" element={<Protected><ScanShareScreen /></Protected>} />
        {/* BLE lamp parents/groups are created here; lamps are paired from the
            device screen. See docs/14-ble-lamps.md. */}
        <Route path="/ble-lamp-group/new" element={<Protected><BleNodeSetupScreen /></Protected>} />
        {/* ── The admin tools, as tabs (ui/shared/AdminToolsLayout) ─────────
            One layout route over all of them, so switching is a tab tap and a
            tool once opened keeps its state while you move between them. The
            gates did NOT move: each screen in `adminScreens` is still wrapped in its own
            AdminOnly, and checks again inside. Firmware writes hardware,
            Tuya Testing rewrites a type and reboots, Message Box publishes
            anything, and the rest write ownership, types and access.
            Tuya Testing is canTuyaTest (AllowTestingTuya), NOT IsSuperAdmin —
            the bench operators granted it are not Super Admins. */}
        <Route element={<Protected><AdminToolsLayout shell="mobile" screens={adminScreens} /></Protected>}>
          {Object.keys(adminScreens).map((path) => (
            <Route key={path} path={path} element={null} />
          ))}
        </Route>
        {/* NOT admin-gated — the phone is where a homeowner adds the sensor they
            have just stuck to the door. The screen scopes them to their own
            locations. */}
        <Route path="/rf-devices" element={<Protected><RfDevicesScreen /></Protected>} />

        {/* NOT admin-gated, and the one that most needed to be here: a wall
            tablet runs THIS shell, so the screen that turns kiosk mode on was
            unreachable from the very device it configures. */}
        <Route path="/wall/kiosk" element={<Protected><KioskSetupScreen /></Protected>} />

        <Route path="/room/:roomId/scenes" element={<Protected><RoomScenesScreen /></Protected>} />
        <Route path="/schedule/:uniqueNumber/:output" element={<Protected><SchedulesScreen /></Protected>} />
        <Route path="/profile" element={<Protected><ProfileScreen /></Protected>} />
        <Route path="/about" element={<Protected><AboutScreen /></Protected>} />
        {/* Kiosk: fills the panel, no app chrome. */}
        {/* One wall editor — /wall/setup. See the note in DesktopRoutes. */}
        <Route path="/wall" element={<Protected bare><WallModeScreen /></Protected>} />
        <Route path="/wall/setup" element={<Protected><WallPanelSetupScreen /></Protected>} />

        {/* A wall tablet lands on its panel instead of the normal home. Only the
            LANDING route is redirected — forcing /wall on every navigation would
            make the designer unreachable from the very tablet it configures. */}
        <Route
          path="*"
          element={
            // eslint-disable-next-line no-nested-ternary
            !isAuthed ? <Navigate to="/login" replace />
              : <Navigate to={kioskLandingRoute() || '/splash'} replace />
          }
        />
      </Routes>
    </MessageDialogProvider>
  );
}
